import { useCallback, useEffect, useMemo, useRef, forwardRef } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useAutomationsSelector } from '__pages/AutomationEngine/store/react';
import {
  getAutomationCustomObjectFields,
  getAutomationCustomObjectCategories,
  getAutomation,
} from '__pages/AutomationEngine/store/selectors';

import { FIELD_TYPES } from 'utility/constants';
import { automationTargetRelationshipOptionsAllowedCardinality } from '__pages/AutomationEngine/dialogs/ActionWizard/subsections/ChangeFieldValue/hooks/useAutomationChangeField';

import Cols from '__components/Layout/Cols';
import {
  SourceVariableType,
  FieldValueVariableType,
  VariableValidation,
  FieldValueSourceSubType,
  FieldValueSourceSubTypes,
} from '../../types';
import { useVariableContext } from '../hooks/useVariableContext';
import {
  AutomationVariableDataType,
  DeletedOption,
  FieldData,
} from 'pages/AutomationEngine/types';
import { VariableErrorType } from '../../VariableWizardContext';

import { FieldValueValidationKeys } from '../ValidationKeys';
import { fieldValidate, FieldValidation } from '../utilties';

import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import { useInputErrors } from '../useInputErrors';
import { useAutomationGetFieldOptions } from '__pages/AutomationEngine/dialogs/ActionWizard/subsections/ChangeFieldValue/hooks/useAutomationGetFieldOptions';
import { fieldsFilter } from '__hooks/useChangeField';
import {
  ApiCategorizedOption,
  CategorizedOption,
  Field,
  OptionWithFieldData,
} from 'pages/AutomationEngine/types';
import { GenericOption } from 'pages/AutomationEngine/types';
import { useQuery } from '@tanstack/react-query';
import { CUSTOM_OBJECTS } from 'queries/query-keys';
import CustomObjectsService from '__services/CustomObjectsService';
import { isEqual } from 'lodash';

const checkMatchingObjectOnRelated = (
  field: Field,
  objectId: string,
  dataType: AutomationVariableDataType
) => {
  if (field.fieldType !== 'relationship' || dataType !== 'entity') {
    return true;
  }
  return field?.relation?.relatedObject === objectId;
};

const getSourceSubTypeOptions = (t: TFunction): FieldValueSourceSubType[] => [
  {
    value: 'this_record_field',
    label: t('Field Value on This Record'),
  },
  {
    value: 'primary_related_object_field',
    label: t('Field Value on a Primary Related Object'),
  },
];

type RelatedOption = GenericOption & {
  relation: any;
};

const getDefaultState = (
  sourceSubtype: FieldValueSourceSubTypes
): FieldValueVariableType => ({
  value: '',
  isValid: true,
  valueCategory: undefined,
  variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
  id: Date.now().toString(),
  sourceSubtype,
  field: undefined,
  relationshipField: undefined,
});

const useComponentState = (
  values: SourceVariableType & {
    sourceErrors: VariableErrorType;
  },
  t: TFunction
) => {
  const automation = useAutomationsSelector(getAutomation);
  const automationContextFields = useAutomationsSelector(
    getAutomationCustomObjectFields
  );
  const automationContextCategories = useAutomationsSelector(
    getAutomationCustomObjectCategories
  );

  const {
    variable,
    updateVariableSource,
    checkVariableFromFieldCompatibility,
    errors,
    setErrors,
  } = useVariableContext(values?.forInitialize!);

  const { dataType } = variable;

  const sourceSubtypeOptions = useMemo(() => getSourceSubTypeOptions(t), [t]);

  const {
    fieldVariable = getDefaultState(sourceSubtypeOptions[0]?.value),
    sourceErrors,
    id,
  } = values;

  const { sourceSubtype, valueCategory, value, field } = fieldVariable!;

  const {
    validate: validateSourceSubtype,
    hasError: hasErrorSourceSubtype,
    clearError: clearSourceSubtypeError,
  } = useInputErrors<FieldValueValidationKeys>(
    id,
    'sourceSubtype',
    sourceErrors,
    errors,
    setErrors
  );

  const {
    validate: validateField,
    hasError: hasErrorField,
    clearError: clearFieldError,
  } = useInputErrors<FieldValueValidationKeys>(
    id,
    'field',
    sourceErrors,
    errors,
    setErrors
  );

  const {
    validate: validateRelationshipField,
    hasError: hasErrorRelationshipField,
    clearError: clearRelationshipFieldError,
  } = useInputErrors<FieldValueValidationKeys>(
    id,
    'relationshipField',
    sourceErrors,
    errors,
    setErrors
  );

  const updateFieldValueVariable = useCallback(
    (fieldVariable: FieldValueVariableType) => {
      updateVariableSource({
        ...values,
        fieldVariable,
      });
    },
    [updateVariableSource, values]
  );

  const setSourceSubtype = useCallback(
    ({ value }: FieldValueSourceSubType | { value: undefined }) => {
      updateFieldValueVariable({
        ...fieldVariable,
        sourceSubtype: value,
        field: undefined,
        relationshipField: undefined,
      });

      clearSourceSubtypeError('sourceSubtype');
      clearFieldError('field');

      clearRelationshipFieldError('relationshipField');
    },
    [
      clearFieldError,
      clearRelationshipFieldError,
      clearSourceSubtypeError,
      fieldVariable,
      updateFieldValueVariable,
    ]
  );

  const setField = useCallback(
    (field: DeletedOption | undefined) => {
      updateFieldValueVariable({
        ...fieldVariable,
        field,
      });
      clearFieldError('field');
    },
    [clearFieldError, fieldVariable, updateFieldValueVariable]
  );

  const setRelationshipField = useCallback(
    (relationshipField: OptionWithFieldData | undefined) => {
      updateFieldValueVariable({
        ...fieldVariable,
        relationshipField,
        field: undefined,
      });
      clearFieldError('field');
      clearRelationshipFieldError('relationshipField');
    },
    [
      clearFieldError,
      clearRelationshipFieldError,
      fieldVariable,
      updateFieldValueVariable,
    ]
  );

  const {
    options: unfilteredOptions,
    relatedOptions,
  }: {
    options: CategorizedOption[];
    relatedOptions: RelatedOption[];
  } = useAutomationGetFieldOptions({
    objectId: automation!.customObject?.id,
    defaultFieldsFilter: fieldsFilter,
    automationContextFields,
    automationContextCategories,
    fieldToModify: null,
  });

  const thisFieldOptions: CategorizedOption[] = useMemo(() => {
    return (unfilteredOptions || [])
      .map((cat: CategorizedOption) => {
        return {
          ...cat,
          options: cat.options
            .filter((opt) => {
              return (
                checkVariableFromFieldCompatibility(opt.fieldData, variable) &&
                checkMatchingObjectOnRelated(
                  opt.fieldData,
                  variable?.dataSubtype?.value!,
                  dataType
                )
              );
            })
            .sort((a, b) => a.label.localeCompare(b.label)),
        };
      })
      .filter(({ options }) => options.length)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    unfilteredOptions,
    checkVariableFromFieldCompatibility,
    dataType,
    variable,
  ]);

  const thisFieldOptionsRef = useRef(thisFieldOptions);

  //as now we can have multiple primary related fields to an automation context object, we need this options to select one
  const [relationshipFieldOptions, relationshipFieldsLookup] = useMemo(() => {
    const lookup: Record<string, FieldData> = {};
    const options = (relatedOptions || [])
      .map(({ label, value }) => {
        const options = (unfilteredOptions || [])
          ?.reduce(
            (acc: any, { options: allOptions }) => [
              ...acc,
              ...allOptions.filter(({ fieldData }) => {
                if (fieldData) {
                  lookup[fieldData.id] = fieldData;
                }
                return (
                  fieldData?.fieldType === FIELD_TYPES.Relationship.type &&
                  fieldData?.relation?.relatedObject === value &&
                  automationTargetRelationshipOptionsAllowedCardinality.includes(
                    fieldData?.relation?.cardinality
                  )
                );
              }),
            ],
            []
          )
          .sort((a: { label: string }, b: { label: string }) =>
            a.label.localeCompare(b.label)
          );

        return {
          label,
          options: options,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [options, lookup];
  }, [relatedOptions, unfilteredOptions]);

  const relationshipField = useMemo(() => {
    const relationshipFieldRaw = fieldVariable.relationshipField;
    if (relationshipFieldRaw && !relationshipFieldRaw.fieldData) {
      return {
        ...relationshipFieldRaw,
        fieldData: relationshipFieldsLookup[relationshipFieldRaw.value],
      };
    }
    return relationshipFieldRaw;
  }, [fieldVariable.relationshipField, relationshipFieldsLookup]);

  const objectId = relationshipField?.fieldData?.relation.relatedObject;

  const {
    data: fieldOnRelatedOptions,
    isLoading: fieldOnRelatedOptionsLoading,
  } = useQuery({
    queryKey: CUSTOM_OBJECTS.CATEGORIZED_FIELDS(objectId),
    queryFn: async () => {
      const { categorizedFields } =
        await CustomObjectsService.getCategorizedModelFields(objectId, {
          settingsSearch: true,
        });

      return categorizedFields;
    },
    enabled: !!objectId,
    select: (res: ApiCategorizedOption[]) => {
      return res.reduce((acc, { name, fields, id }) => {
        const fieldsToDisplay = fields
          .filter(
            (field) =>
              checkVariableFromFieldCompatibility(field, variable) &&
              checkMatchingObjectOnRelated(
                field,
                variable?.dataSubtype?.value!,
                dataType
              )
          )
          .sort((a, b) => String(a.displayName).localeCompare(b.displayName));

        return fieldsToDisplay.length
          ? [
              ...acc,
              {
                label: name,
                key: id,
                options: fieldsToDisplay
                  .map((field) => ({
                    label: field.displayName,
                    value: field.id,
                    fieldData: field,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              },
            ]
          : acc;
      }, [] as CategorizedOption[]);
    },
  });

  const fieldOnRelatedOptionsRef = useRef(fieldOnRelatedOptions);

  // clear field selected if there is a change to the options and it's no longer included
  useEffect(() => {
    if (
      !isEqual(thisFieldOptions, thisFieldOptionsRef.current) &&
      sourceSubtype === 'this_record_field'
    ) {
      const allOptions = thisFieldOptions
        .flatMap(({ options }) => options)
        .map(({ value }) => value);

      const isDeleted = field?.deleted === true;

      if (
        field?.value &&
        !isDeleted &&
        !allOptions.includes(field?.value || '')
      ) {
        setField(undefined);
      }

      thisFieldOptionsRef.current = thisFieldOptions;
    }
  }, [field, setField, thisFieldOptions, sourceSubtype]);

  // clear field selected if there is a chnage to the options and it's no longer included
  useEffect(() => {
    if (
      fieldOnRelatedOptions &&
      !isEqual(fieldOnRelatedOptions, fieldOnRelatedOptionsRef.current) &&
      sourceSubtype === 'primary_related_object_field'
    ) {
      const allOptions = fieldOnRelatedOptions
        .flatMap(({ options }) => options)
        .map(({ value }) => value);
      const isDeleted = field?.deleted === true;
      if (
        field?.value &&
        !isDeleted &&
        !allOptions.includes(field?.value || '')
      ) {
        setField(undefined);
      }

      fieldOnRelatedOptionsRef.current = fieldOnRelatedOptions;
    }
  }, [field, setField, fieldOnRelatedOptions, sourceSubtype]);

  return {
    sourceSubtypeOptions,
    sourceSubtype,
    setSourceSubtype,
    validateSourceSubtype,
    hasErrorSourceSubtype,

    // ====> field
    thisFieldOptions,
    field,
    setField,
    validateField,
    hasErrorField,

    // ====> Relationship Field
    relationshipField,
    relationshipFieldOptions,
    setRelationshipField,
    validateRelationshipField,
    hasErrorRelationshipField,

    // ====> Relationship Field Options
    fieldOnRelatedOptions,
    fieldOnRelatedOptionsLoading,

    valueCategory,
    value,
  };
};

export const Component = forwardRef(
  (
    values: SourceVariableType & {
      sourceErrors: VariableErrorType;
      dataType: AutomationVariableDataType;
      dataSubtype: any;
    },
    ref
  ) => {
    const { t } = useTranslation();

    const {
      sourceSubtypeOptions,
      sourceSubtype,
      setSourceSubtype,
      validateSourceSubtype,
      hasErrorSourceSubtype,

      // ====> Field
      thisFieldOptions,
      field,
      setField,
      validateField,
      hasErrorField,

      // ====> Relationship Field
      relationshipFieldOptions,
      relationshipField,
      setRelationshipField,
      validateRelationshipField,
      hasErrorRelationshipField,

      // ====> Relationship Field Options
      fieldOnRelatedOptions,
      fieldOnRelatedOptionsLoading,
    } = useComponentState(values, t);

    return (
      <Cols columns={3} gutter="20px" ref={ref}>
        <Select
          label={t('Value Source')}
          placeholder={t('Choose Value Source')}
          value={sourceSubtypeOptions.find(
            ({ value }) => value === sourceSubtype
          )}
          options={sourceSubtypeOptions}
          onChange={setSourceSubtype}
          validate={validateSourceSubtype}
          error={hasErrorSourceSubtype}
          menuInline
          data-qa="field-value-value-source"
        />
        {sourceSubtype !== 'primary_related_object_field' ? (
          <Select
            label={t('Field')}
            placeholder={t('Choose Field')}
            value={field}
            options={thisFieldOptions}
            onChange={setField}
            validate={validateField}
            error={hasErrorField}
            menuInline
            disabled={sourceSubtype !== 'this_record_field'}
            data-qa="field-value-field"
          />
        ) : (
          <>
            <Select
              label={t('Relationship Field')}
              placeholder={t('Choose Relationship Field')}
              value={relationshipField}
              options={relationshipFieldOptions}
              onChange={setRelationshipField}
              validate={validateRelationshipField}
              error={hasErrorRelationshipField}
              menuInline
              data-qa="field-value-relationship-field"
            />
            <Select
              label={t('Related Record Field')}
              placeholder={t('Choose Related Record Field')}
              value={field}
              options={fieldOnRelatedOptions}
              onChange={setField}
              validate={validateField}
              error={hasErrorField}
              isLoading={fieldOnRelatedOptionsLoading}
              menuInline
              disabled={!relationshipField}
              data-qa="field-value-related-field"
            />
          </>
        )}
      </Cols>
    );
  }
);

const getFieldsToValidate = (t: TFunction): FieldValidation => ({
  sourceSubtype: [{ type: 'required', fieldType: 'entity', isSelect: true }],
  field: [
    {
      type: 'required',
      fieldType: 'entity',
      condition: {
        sourceSubtype: ['this_record_field', 'primary_related_object_field'],
      },
      isSelect: true,
    },
    {
      type: 'deleted-field',
      fieldType: undefined,
      deletedNameLookup: { fallback: t('Field') },
    },
  ],

  relationshipField: [
    {
      type: 'required',
      fieldType: 'entity',
      condition: {
        sourceSubtype: ['primary_related_object_field'],
      },
      isSelect: true,
    },
    {
      type: 'deleted-field',
      fieldType: undefined,
      deletedNameLookup: { fallback: t('Field') },
    },
  ],
});

export const validate = ({ values, checkRequired, t }: VariableValidation) => {
  const { fieldVariable, sourceSubtype } = values;

  return fieldValidate(
    fieldVariable,
    getFieldsToValidate(t),
    sourceSubtype,
    checkRequired,
    t
  );
};
